/**
 * AI Routing Configuration UI
 * Manage geo-based routing rules
 */

import { BaseComponent } from './components/base-component';
import type { APIClient } from './api';

interface GeoRule {
  countries?: string[];
  continents?: string[];
  models: string[];
  reason: string;
}

interface AIRoutingConfig {
  routing: {
    geo_rules: GeoRule[];
    default: {
      models: string[];
    };
  };
  features: {
    geo_routing_enabled: boolean;
    fallback_enabled: boolean;
  };
}

export class AIRoutingUI extends BaseComponent {
  private apiClient: APIClient;
  private config: AIRoutingConfig | null = null;
  private loading = false;

  constructor(container: HTMLElement, apiClient: APIClient) {
    super(container);
    this.apiClient = apiClient;
  }

  async render(): Promise<void> {
    this.container.innerHTML = `
      <div class="routing-config-ui">
        <div class="routing-header">
          <h2>🌍 AI Geo Routing Configuration</h2>
          <p class="subtitle">Geo-based model selection rules</p>
        </div>

        <div class="routing-toolbar">
          <div class="toolbar-left">
            <button id="reset-btn" class="btn btn-danger">
              <span>🔄</span> Reset to Default
            </button>
          </div>
          <div class="toolbar-right">
            <button id="refresh-btn" class="btn btn-secondary">
              <span>🔄</span> Refresh
            </button>
          </div>
        </div>

        <div id="routing-container" class="routing-container">
          <div class="loading">Loading AI routing...</div>
        </div>
      </div>
    `;

    document.getElementById('refresh-btn')?.addEventListener('click', () => this.loadRouting());
    document.getElementById('reset-btn')?.addEventListener('click', () => this.resetToDefault());
    await this.loadRouting();
  }

  private async loadRouting(): Promise<void> {
    this.loading = true;
    const container = document.getElementById('routing-container');
    if (!container) return;

    container.innerHTML = '<div class="loading">⏳ Loading...</div>';

    try {
      const response = await this.apiClient.get<AIRoutingConfig>('/v1/admin/config/ai-routing');
      this.config = response.data;
      this.renderRouting();
    } catch (error) {
      container.innerHTML = `
        <div class="error">
          <p>❌ Failed to load AI routing</p>
          <p class="error-detail">${error instanceof Error ? error.message : 'Unknown'}</p>
        </div>
      `;
    } finally {
      this.loading = false;
    }
  }

  private renderRouting(): void {
    const container = document.getElementById('routing-container');
    if (!container || !this.config) return;

    container.innerHTML = `
      <div class="routing-features">
        <div class="feature-toggle">
          <span class="toggle-label">🌐 Geo Routing:</span>
          <span class="toggle-value ${this.config.features.geo_routing_enabled ? 'enabled' : 'disabled'}">
            ${this.config.features.geo_routing_enabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>
        <div class="feature-toggle">
          <span class="toggle-label">🔄 Fallback Chain:</span>
          <span class="toggle-value ${this.config.features.fallback_enabled ? 'enabled' : 'disabled'}">
            ${this.config.features.fallback_enabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>
      </div>

      <div class="geo-rules-section">
        <h3>Geo Rules (evaluated in order)</h3>
        <div class="geo-rules-grid">
          ${this.config.routing.geo_rules.map((rule, idx) => this.renderGeoRule(rule, idx + 1)).join('')}
        </div>
      </div>

      <div class="default-routing-section">
        <h3>Default Routing (no geo match)</h3>
        <div class="default-card">
          <strong>Fallback Models:</strong>
          <ol class="model-priority-list">
            ${this.config.routing.default.models.map((m, i) =>
              `<li><code>${m}</code> ${i === 0 ? '⭐' : ''}</li>`
            ).join('')}
          </ol>
        </div>
      </div>
    `;
  }

  private renderGeoRule(rule: GeoRule, priority: number): string {
    const location = rule.countries
      ? `🌏 ${rule.countries.join(', ')}`
      : rule.continents
      ? `🌍 ${rule.continents.join(', ')}`
      : '🌎 Global';

    return `
      <div class="geo-rule-card">
        <div class="rule-header">
          <span class="rule-priority">#${priority}</span>
          <span class="rule-location">${location}</span>
        </div>
        <p class="rule-reason">${rule.reason}</p>
        <div class="rule-models">
          <strong>Model Chain:</strong>
          <ol class="model-priority-list">
            ${rule.models.map((m, i) =>
              `<li><code>${m}</code> ${i === 0 ? '⭐' : ''}</li>`
            ).join('')}
          </ol>
        </div>
      </div>
    `;
  }

  private async resetToDefault(): Promise<void> {
    if (!confirm('Reset AI routing to hardcoded defaults?\n\nThis will remove all KV overrides.')) {
      return;
    }

    try {
      await this.apiClient.delete('/v1/admin/config/ai-routing');
      alert('✅ AI routing reset to defaults!');
      await this.loadRouting();
    } catch (error) {
      alert(`❌ Failed: ${error instanceof Error ? error.message : 'Unknown'}`);
    }
  }

  destroy(): void {
    this.container.innerHTML = '';
  }
}
